import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useSession } from "../auth/AuthProvider";
import { formatJalali } from "../lib/format";

interface BookingDoc {
  id: string;
  date: string;
  shift: "MORNING" | "EVENING" | "FULL_DAY";
  status: string;
  guestCount: number;
}

const SHIFT_LABEL: Record<string, string> = {
  MORNING: "صبح",
  EVENING: "شب",
  FULL_DAY: "تمام روز",
};

/** Month reservation grid — the operational heart of the owner panel (docs/06 §5). */
export function CalendarPage() {
  const { orgId } = useSession();
  const [anchor, setAnchor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [bookings, setBookings] = useState<BookingDoc[]>([]);

  const monthStart = anchor.toISOString().slice(0, 10);
  const monthEnd = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);

  useEffect(() => {
    if (!orgId) return;
    // Realtime: reservation changes from any device appear instantly.
    const q = query(
      collection(db, "bookings"),
      where("orgId", "==", orgId),
      where("date", ">=", monthStart),
      where("date", "<=", monthEnd),
    );
    return onSnapshot(q, (snap) => {
      setBookings(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as Omit<BookingDoc, "id">) }))
          .filter((b) => b.status !== "cancelled"),
      );
    });
  }, [orgId, monthStart, monthEnd]);

  const byDate = useMemo(() => {
    const map = new Map<string, BookingDoc[]>();
    for (const booking of bookings) {
      map.set(booking.date, [...(map.get(booking.date) ?? []), booking]);
    }
    return map;
  }, [bookings]);

  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const date = new Date(anchor.getFullYear(), anchor.getMonth(), i + 1);
    return date.toISOString().slice(0, 10);
  });

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton
          onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
        >
          <ChevronRightIcon />
        </IconButton>
        <Typography variant="h6" sx={{ minWidth: 220, textAlign: "center" }}>
          {formatJalali(monthStart)} — {formatJalali(monthEnd)}
        </Typography>
        <IconButton
          onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
        >
          <ChevronLeftIcon />
        </IconButton>
      </Stack>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", lg: "repeat(7, 1fr)" }}
        gap={1}
      >
        {days.map((date) => {
          const dayBookings = byDate.get(date) ?? [];
          const full =
            dayBookings.some((b) => b.shift === "FULL_DAY") || dayBookings.length >= 2;
          return (
            <Paper
              key={date}
              variant="outlined"
              sx={{
                p: 1,
                minHeight: 84,
                bgcolor: full ? "error.50" : dayBookings.length > 0 ? "warning.50" : "inherit",
                borderColor: full ? "error.light" : "divider",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatJalali(date)}
              </Typography>
              <Stack spacing={0.5} mt={0.5}>
                {dayBookings.map((b) => (
                  <Tooltip key={b.id} title={`${b.guestCount} مهمان · ${b.status}`}>
                    <Chip
                      size="small"
                      label={SHIFT_LABEL[b.shift] ?? b.shift}
                      color={b.status === "confirmed" ? "primary" : "warning"}
                      variant={b.status === "confirmed" ? "filled" : "outlined"}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </>
  );
}
